import React, { useRef, useEffect, useImperativeHandle, forwardRef, useState } from 'react';
import styles from './VideoPlayer.module.scss';
import { audio } from "services/audioManager";

const VideoPlayer = forwardRef(({ video, show, onEnded, onError, className, posterImage: posterImageProp, showSkipButton = false, onPlay, onUpdateStill, forceMute = false }, ref) => {
    const videoRef = useRef(null);
    const [error, setError] = useState(null);
    const [retryCount, setRetryCount] = useState(0);
    const [isLoading, setIsLoading] = useState(true);
    const [isPlaying, setIsPlaying] = useState(false);
    const [hasEnded, setHasEnded] = useState(false);
    const [firstFrameLoaded, setFirstFrameLoaded] = useState(false);
    const [showSpinner, setShowSpinner] = useState(false);
    const [isMuted, setIsMuted] = useState(false);
    const [usingFallbackAudio, setUsingFallbackAudio] = useState(false);
    const [updatedStill, setUpdatedStill] = useState(false);

    const spinnerTimeoutRef = useRef(null);
    const fallbackAudioPlayPromise = useRef(null);
    const maxRetries = 3;
    const autoplayBlockedRef = useRef(false);

    // Parse video prop to get src, fallbackAudio, and posterImage
    const videoSrc = typeof video === 'string' ? video : video?.src;
    const volume = typeof video === 'object' ? video?.volume ?? 1.0 : 1.0;
    const fallbackAudio = typeof video === 'object' ? video?.fallbackAudio : null;
    const fallbackAudioVolume = typeof fallbackAudio === 'object' ? fallbackAudio?.volume ?? 1.0 : 1.0;
    const fallbackAudioSrc = typeof fallbackAudio === 'string' ? fallbackAudio : fallbackAudio?.src;
    const posterImage = (typeof video === 'object' ? video?.posterImage : null) || posterImageProp;

    useEffect(() => {
        const handleFirstInteraction = () => {
            // If autoplay was blocked, try playing on first interaction
            if (autoplayBlockedRef.current && videoRef.current) {
                videoRef.current.play().catch(err => {
                    console.error('Still unable to play after interaction:', err);
                });
            }
            document.removeEventListener('click', handleFirstInteraction);
            document.removeEventListener('keydown', handleFirstInteraction);
        };

        document.addEventListener('click', handleFirstInteraction);
        document.addEventListener('keydown', handleFirstInteraction);

        return () => {
            document.removeEventListener('click', handleFirstInteraction);
            document.removeEventListener('keydown', handleFirstInteraction);
        };
    }, []);

    const handleError = (e) => {
        const vid = videoRef.current;

        // Handle play() promise rejection
        if (e instanceof Error) {
            if (e.name === 'NotAllowedError') {
                console.log('Autoplay blocked - muting video and using fallback audio');
                autoplayBlockedRef.current = true;

                // Mute video and use fallback audio if available
                if (vid) {
                    setIsMuted(true);
                    vid.muted = true;

                    // Start fallback audio using AudioManager
                    if (fallbackAudioSrc) {
                        setUsingFallbackAudio(true);
                        fallbackAudioPlayPromise.current = audio.playVo(fallbackAudioSrc, {
                            volume: fallbackAudioVolume,
                            onEnd: () => {
                                console.log('Fallback audio ended');
                            }
                        });
                    }

                    vid.play().catch(err => {
                        console.error('Failed to play even when muted:', err);
                        // Now treat it as a real error
                        const errorDetails = {
                            code: 5,
                            message: `Play error: ${err.message}`,
                            src: videoSrc,
                            networkState: vid?.networkState,
                            readyState: vid?.readyState
                        };
                        setError(errorDetails);
                        onError?.(errorDetails);
                    });
                }
                return;
            }

            // Other play errors
            const errorDetails = {
                code: 5,
                message: `Play error: ${e.message}`,
                src: videoSrc,
                networkState: vid?.networkState,
                readyState: vid?.readyState
            };

            console.error('Video Play Error:', errorDetails);
            setError(errorDetails);
            onError?.(errorDetails);
            return;
        }

        // Handle video element error events (original logic)
        if (!vid || !videoSrc) return;

        setIsLoading(false);

        const errorDetails = {
            code: vid.error?.code,
            message: getErrorMessage(vid.error?.code),
            src: videoSrc,
            networkState: vid.networkState,
            readyState: vid.readyState
        };

        console.error('Video Error:', errorDetails);
        setError(errorDetails);

        if (retryCount < maxRetries) {
            console.log(`Retrying video load (${retryCount + 1}/${maxRetries})...`);
            setTimeout(() => {
                setRetryCount(prev => prev + 1);
                vid.load();
            }, 1000 * (retryCount + 1));
        } else {
            onError?.(errorDetails);
        }
    };

    const getErrorMessage = (code) => {
        switch (code) {
            case 1:
                return 'MEDIA_ERR_ABORTED: Video loading was aborted';
            case 2:
                return 'MEDIA_ERR_NETWORK: Network error occurred';
            case 3:
                return 'MEDIA_ERR_DECODE: Video decoding failed';
            case 4:
                return 'MEDIA_ERR_SRC_NOT_SUPPORTED: Video format not supported';
            default:
                return 'Unknown video error';
        }
    };

    useImperativeHandle(ref, () => ({
        skip: handleSkip,
        getError: () => error,
        isLoading: () => isLoading,
        isPlaying: () => isPlaying,
        isMuted: () => isMuted,
        usingFallbackAudio: () => usingFallbackAudio
    }));

    const handleLoadedMetadata = () => {
        console.log('handleLoadedMetadata - seeking to first frame, videoSrc:', videoSrc);
        if (videoRef.current) {
            // Explicitly seek to ensure first frame is rendered
            videoRef.current.currentTime = 0;
        }
    };

    const handleSeeked = () => {
        console.log('handleSeeked - first frame seeked, videoSrc:', videoSrc);
        if (!videoRef.current || firstFrameLoaded) return;

        // Wait for the frame to actually be painted to the screen
        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                console.log('Video first frame painted');
                setFirstFrameLoaded(true);

                // Only start playing if show is true
                if (videoRef.current && show) {
                    videoRef.current.play().catch(err => {
                        handleError(err);
                    });
                }
            });
        });
    };

    const handleEnded = () => {
        console.log('handleEnded - videoSrc:', videoSrc);

        // Stop fallback audio if it's playing
        if (usingFallbackAudio && fallbackAudioPlayPromise.current?.handle) {
            fallbackAudioPlayPromise.current.handle.stop(100);
            fallbackAudioPlayPromise.current = null;
        }

        setHasEnded(true);
        onEnded();
    };

    const handleCanPlay = () => {
    };

    const handlePlaying = () => {
        console.log('handlePlaying - videoSrc:', videoSrc);
        if (!videoSrc) return;
        clearTimeout(spinnerTimeoutRef.current);
        setIsPlaying(true);
        setIsLoading(false);
        setShowSpinner(false);
        if (onPlay) onPlay();
    };

    const handleWaiting = () => {
        console.log('handleWaiting - videoSrc:', videoSrc);
        if (!videoSrc) return;

        if (videoRef.current) {
            const timeRemaining = videoRef.current.duration - videoRef.current.currentTime;
            if (timeRemaining <= 0) return;
        }

        setIsLoading(true);
        spinnerTimeoutRef.current = setTimeout(() => {
            setShowSpinner(true);
        }, 5000);
    };

    const handleLoadStart = () => {
        console.log('handleLoadStart - videoSrc:', videoSrc);
        if (!videoSrc) return;
        setIsLoading(true);
        setIsPlaying(false);
        spinnerTimeoutRef.current = setTimeout(() => {
            setShowSpinner(true);
        }, 5000);
    };

    const handleSkip = () => {
        console.log('handleSkip - videoSrc:', videoSrc);

        // Stop fallback audio
        if (usingFallbackAudio && fallbackAudioPlayPromise.current?.handle) {
            fallbackAudioPlayPromise.current.handle.stop(100);
            fallbackAudioPlayPromise.current = null;
        }

        if (videoRef.current?.currentTime) {
            videoRef.current.currentTime = videoRef.current.duration;
        }
        onEnded();
    };

    useEffect(() => {
        if (videoRef.current && videoSrc && show) {

            setError(null);
            setRetryCount(0);
            setIsLoading(true);
            setIsPlaying(false);
            setShowSpinner(false);
            setHasEnded(false);
            setIsMuted(false);
            setFirstFrameLoaded(false);
            setUsingFallbackAudio(false);
            setUpdatedStill(false);
            autoplayBlockedRef.current = false;
            clearTimeout(spinnerTimeoutRef.current);

            videoRef.current.volume = volume;

            // Stop any playing fallback audio
            if (fallbackAudioPlayPromise.current?.handle) {
                fallbackAudioPlayPromise.current.handle.stop(0);
                fallbackAudioPlayPromise.current = null;
            }

            videoRef.current.load();
        }

        // Pause video when show becomes false
        if (videoRef.current && !show) {
            videoRef.current.pause();

            // Stop fallback audio when hiding
            if (fallbackAudioPlayPromise.current?.handle) {
                fallbackAudioPlayPromise.current.handle.stop(100);
                fallbackAudioPlayPromise.current = null;
            }
        }

        return () => {
            clearTimeout(spinnerTimeoutRef.current);
            // Clean up fallback audio
            if (fallbackAudioPlayPromise.current?.handle) {
                fallbackAudioPlayPromise.current.handle.stop(0);
                fallbackAudioPlayPromise.current = null;
            }
        };
    }, [videoSrc, show]);


    const handleTimeUpdate = () => {
        const video = videoRef.current;
        if (!video || updatedStill) return;

        if (video.currentTime >= 1) {
            setUpdatedStill(true);
            if (onUpdateStill) onUpdateStill();
        }
    };

    return (
        <div className={`${styles.videoContainer} ${className || ''}`} data-show={show}>
            {posterImage && (
                <div className={styles.posterContainer} data-show={(!firstFrameLoaded) && show && !hasEnded}>
                    <img src={posterImage} alt="Video loading" className={styles.posterImage} />
                    {showSpinner && <div className={styles.loadingSpinner} />}
                </div>
            )}

            <video ref={videoRef} className={styles.videoPlayer} data-show={show} playsInline onEnded={handleEnded} onError={handleError}
                onCanPlay={handleCanPlay} onPlaying={handlePlaying} onWaiting={handleWaiting} onLoadStart={handleLoadStart}
                onLoadedMetadata={handleLoadedMetadata} onSeeked={handleSeeked} muted={isMuted ? isMuted : forceMute} onTimeUpdate={handleTimeUpdate}
                disableremoteplayback x-webkit-airplay="deny" playsinline>
                {videoSrc && <source src={videoSrc} type="video/mp4"/>}
                Your browser does not support the video tag.
            </video>

            {showSkipButton && (
                <button className={styles.skipButton} data-show={show && !error} onClick={handleSkip} aria-label="Skip video" >
                    Skip
                </button>
            )}

            {error && retryCount >= maxRetries && (
                <div className={styles.errorOverlay} data-show={show}>
                    <div className={styles.errorContent}>
                        <h3>Video Error</h3>
                        <p>{error.message}</p>
                        <div className={styles.errorButtons}>
                            <button onClick={() => {
                                setError(null);
                                setRetryCount(0);
                                videoRef.current?.load();
                            }}>
                                Try Again
                            </button>
                            <button onClick={handleSkip}>
                                Skip Video
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
});

VideoPlayer.displayName = 'VideoPlayer';

export default VideoPlayer;